import { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm as useFormV7 } from 'react-hook-form-v7';

import { RoleFormPageProps, RoleFormData, Breadcrumb } from '@personnel/presentation/types';
import { PageLayout } from './PageLayout';
import { SecondaryHeader } from './SecondaryHeader';
import { Breadcrumbs } from './Breadcrumbs';
import { RoleForm } from '@personnel/presentation/components/form';
import { useRoleSchema } from '@personnel/presentation/components/form/schema';
import { useCreateRole, useUpdateRole, useRole } from '@personnel/presentation/hooks';
import { isAxiosError, hasApiErrorResponse, extractErrorInfo } from '@personnel/application/errors';
import { ERROR_CODES } from '@personnel/domain/errors/errorCodes';

/**
 * Role Form Page Component
 *
 * Shared page component for create, edit, and duplicate role flows.
 * Manages form state, data loading, and submission logic.
 *
 * @param {RoleFormPageProps} props - Component properties
 * @returns {React.ReactElement} Role form page component
 *
 * @example
 * // Create mode
 * <RoleFormPage mode="create" />
 *
 * @example
 * // Edit mode
 * <RoleFormPage mode="edit" roleUuid="abc-123" />
 *
 * @example
 * // Duplicate mode
 * <RoleFormPage mode="duplicate" roleUuid="abc-123" />
 */
export const RoleFormPage = ({ mode, roleUuid }: RoleFormPageProps): React.ReactElement => {
  const { t } = useTranslation('roles');
  const { t: tPersonnel } = useTranslation('personnel');
  const history = useHistory();

  // React Query mutations
  const { mutateAsync: createRole, isLoading: isCreating } = useCreateRole();
  const { updateRole, isLoading: isUpdating } = useUpdateRole();

  // Determine if role data should be loaded (for edit or duplicate modes)
  const shouldLoadRole = mode !== 'create' && !!roleUuid;

  // Combined loading state
  const isMutating = isCreating || isUpdating;

  // ✅ Fetch role data using React Query (replaces useEffect + setState)
  const { role, isLoading: isLoadingRole, isError: isErrorLoadingRole } = useRole(roleUuid || '', {
    enabled: shouldLoadRole,
  });

  // Initialize form with validation schema
  const { resolver } = useRoleSchema();
  const methods = useFormV7<RoleFormData>({
    resolver: resolver as any,
    mode: 'onChange', // Validate on change for real-time button state
    defaultValues: {
      name: '',
      scope: 'organization',
      permissions: [],
    },
  });

  // ✅ Populate form when role data is loaded
  useEffect(() => {
    if (!role) return;

    const formData: Partial<RoleFormData> = {
      name: mode === 'duplicate' ? '' : role.name,
      scope: 'organization', // TODO: Map from role.permissions level
      permissions: role.permissions.map((p) => p.frontendId) as RoleFormData['permissions'],
    };

    methods.reset(formData);
  }, [role, mode, methods]);

  // Configuration based on mode
  const config = {
    create: {
      title: t('create.title'),
      description: t('create.description'),
      breadcrumbs: [
        { label: tPersonnel('breadcrumbs.personal'), path: '/roles' },
        { label: t('create.title') },
      ] as Breadcrumb[],
      buttonLabel: t('list.createButton'),
      loadingLabel: t('create.loading'),
    },
    edit: {
      title: t('edit.title'),
      description: t('edit.description'),
      breadcrumbs: [
        { label: tPersonnel('breadcrumbs.roles'), path: '/roles' },
        { label: t('edit.title') },
      ] as Breadcrumb[],
      buttonLabel: t('edit.saveButton'),
      loadingLabel: t('edit.loading'),
    },
    duplicate: {
      title: t('duplicate.title'),
      description: t('create.description'), // Reuse create description for duplicate
      breadcrumbs: [
        { label: tPersonnel('breadcrumbs.roles'), path: '/roles' },
        { label: t('duplicate.title') },
      ] as Breadcrumb[],
      buttonLabel: t('list.createButton'),
      loadingLabel: t('create.loading'),
    },
  }[mode];

  const handleSubmit = useCallback(
    async (data: RoleFormData) => {
      try {
        if (mode === 'edit' && roleUuid) {
          // ✅ Update existing role
          await updateRole({
            uuid: roleUuid,
            data: {
              name: data.name,
              permissions: data.permissions,
            },
          });
        } else {
          // Create or duplicate
          await createRole({
            name: data.name,
            permissions: data.permissions,
          });
        }

        // Navigate back to roles list
        history.push('/roles');
      } catch (error) {
        // Check if it's a "role already exists" error (409)
        // This error should be shown in the form field, not as a toast
        if (isAxiosError(error) && hasApiErrorResponse(error)) {
          const statusCode = error.response?.status || 500;
          const apiErrorResponse = error.response?.data;

          if (apiErrorResponse) {
            const errorInfo = extractErrorInfo(apiErrorResponse, statusCode);

            // Handle "role already exists" error in the form field
            if (errorInfo.code === ERROR_CODES.ROLES.ROLE_ALREADY_EXISTS) {
              methods.setError('name', {
                type: 'manual',
                message: t('errors.RoleAlreadyExists'),
              });
              return; // Don't show toast, error is shown in form field
            }
          }
        }

        // Other errors: toast shown automatically by useCreateRole/useUpdateRole
        // eslint-disable-next-line no-console
        console.error('Error submitting role:', error);
      }
    },
    [mode, roleUuid, history, createRole, updateRole, methods, t],
  );

  const handleActionButtonClick = useCallback(() => {
    // Trigger form submit
    const submitButton = document.getElementById('role-form-submit');
    submitButton?.click();
  }, []);

  // Button disabled logic based on mode
  const { isValid, isDirty } = methods.formState;
  const isButtonDisabled =
    mode === 'create'
      ? !isValid // Create: only needs to be valid
      : !isValid || !isDirty; // Edit/Duplicate: needs to be valid AND modified

  // Show error state if role failed to load
  if (isErrorLoadingRole && shouldLoadRole) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-sm text-destructive mb-4">{t('errors.roleNotFound')}</p>
          <button className="text-sm text-primary underline" onClick={() => history.push('/roles')} type="button">
            {t('common.backToList')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <PageLayout>
      {/* Breadcrumbs Navigation */}
      <Breadcrumbs items={config.breadcrumbs} />

      {/* Secondary Header: Page-level with action */}
      <SecondaryHeader
        actionButton={{
          label: config.buttonLabel,
          onClick: handleActionButtonClick,
          disabled: isButtonDisabled || isMutating,
          loading: isMutating,
          loadingLabel: config.loadingLabel,
        }}
        description={config.description}
        title={config.title}
      />

      {/* Form Content */}
      <RoleForm
        disabled={isLoadingRole}
        isSubmitting={isMutating}
        methods={methods}
        onSubmit={handleSubmit}
        submitLabel={config.buttonLabel}
      />
    </PageLayout>
  );
};
